import { useCallback, useMemo, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { Button, ConfirmDialog } from "@legends/ui";
import AdminModal from "./admin/AdminModal";
import {
  AllCommunityModule,
  ModuleRegistry,
  themeQuartz,
  type CellValueChangedEvent,
  type ColDef,
  type GetRowIdParams,
  type GridApi,
  type GridReadyEvent,
  type ICellRendererParams,
} from "ag-grid-community";

// AG Grid Community (OSS) is our single grid across the admin/operator surface — a
// deliberate, documented exception to the "@legends/ui component-first" rule, scoped to
// data grids (ADR 0156). Clipboard/range-select are AG Grid Enterprise features and are
// intentionally NOT included (OSS-clean); everything else — sorting, filtering, inline
// editing, a pinned add-row, inline delete, keyboard nav — is Community.
ModuleRegistry.registerModules([AllCommunityModule]);

// The Cave theme, expressed through AG Grid v33's Theming API (no CSS import, no legacy
// theme) so the grid matches the gold-on-stone language: brand gold accent, stone
// surfaces, cream text, gold-tinted hairlines.
export const caveGridTheme = themeQuartz.withParams({
  accentColor: "#c9a227", // brand gold
  // Render the grid's native scroll viewports in dark mode so the browser draws
  // dark scrollbars (covers Firefox + the always-on macOS/Windows classic bar) —
  // the Quartz default left a pale native scrollbar that clashed with the dark
  // surface. The .cave-grid ::-webkit-scrollbar rules (index.css) skin it gold.
  browserColorScheme: "dark",
  backgroundColor: "#141519",
  foregroundColor: "#ece7dd",
  textColor: "#ece7dd",
  borderColor: "rgba(255,255,255,0.07)",
  chromeBackgroundColor: "#1c1d22",
  headerBackgroundColor: "#1c1d22",
  headerTextColor: "#a49c90",
  headerFontWeight: 600,
  oddRowBackgroundColor: "rgba(255,255,255,0.018)",
  rowHoverColor: "rgba(201,162,39,0.08)",
  selectedRowBackgroundColor: "rgba(201,162,39,0.14)",
  rangeSelectionBorderColor: "#c9a227",
  inputBackgroundColor: "#0e0e11",
  inputBorder: { color: "rgba(201,162,39,0.35)", width: 1 },
  inputFocusBorder: { color: "#c9a227", width: 1 },
  fontFamily: "inherit",
  // Dense by default: body text sized close to the header row, and the base spacing
  // (default 8) pulled well down so padding/row-height are tight and more fits.
  fontSize: 12,
  headerFontSize: 11,
  spacing: 5,
  rowVerticalPaddingScale: 0.7,
  cellHorizontalPaddingScale: 0.85,
  borderRadius: 6,
  wrapperBorderRadius: 8,
});

export interface DataGridProps<T extends object> {
  rows: T[];
  columns: ColDef<T>[];
  /** Stable id per row — enables inline edit/delete + smooth transactions. */
  getRowId: (row: T) => string;
  /** Persist a single inline cell edit (field + new value on the row). */
  onEdit?: (row: T, field: string, value: unknown) => void | Promise<void>;
  /** Inline delete of a data row (adds a trailing ✕ action column). */
  onDelete?: (row: T) => void | Promise<void>;
  /** Commit the pinned top add-row. Its presence renders the pinned add-row +
   *  a ＋ action; `addTemplate` is the blank draft. Return false to keep the draft. */
  onAdd?: (draft: T) => boolean | void | Promise<boolean | void>;
  addTemplate?: T;
  /** Create via a "＋ Add" button + modal form (ADR 0160, Phase 4) instead of the
   *  pinned add-row. Opt-in — omit to keep the inline pinned row. The modal form is
   *  derived from the editable columns. */
  addModal?: boolean;
  /** Noun for the add button/modal, e.g. "cave" → "＋ Add a cave". */
  addLabel?: string;
  /** Consumer-owned create modal (Phase 4): when set, the "＋ Add" button renders
   *  this instead of the generic column-derived form — for grids whose create
   *  fields don't map 1:1 to columns (derived/hidden fields). `close` dismisses it. */
  renderAddModal?: (opts: { close: () => void }) => React.ReactNode;
  /** Turn a row's ✕ into a guarded delete: return the confirmation message for
   *  that row (a ConfirmDialog opens first). Omit for immediate inline delete. */
  confirmDeleteMessage?: (row: T) => string;
  /** Paginate the grid (ADR 0160, Phase 4) — for potentially large lists. */
  paginated?: boolean;
  pageSize?: number;
  /** Fixed pixel height (scrolls); omit for auto-height that grows with the rows. */
  height?: number;
  /** aria-label for the grid region. */
  label?: string;
  emptyText?: string;
  /** Global quick-filter text (in addition to the per-column floating filters). */
  quickFilterText?: string;
}

const ACTION_COL = "__actions";

/**
 * DataGrid — the Cave-themed AG Grid wrapper (ADR 0156). Sorting + a floating filter row
 * are on by default; columns opt into inline editing via standard ColDefs
 * (`editable: true` + `cellEditor: "agSelectCellEditor" | "agCheckboxCellEditor" | …`).
 * A pinned top row replaces the old above-grid "add" forms, and a trailing ✕ deletes a
 * row inline. Delete-key on a focused row deletes it; Enter in the add-row commits it.
 */
export default function DataGrid<T extends object>({
  rows,
  columns,
  getRowId,
  onEdit,
  onDelete,
  onAdd,
  addTemplate,
  addModal = false,
  addLabel,
  renderAddModal,
  confirmDeleteMessage,
  paginated = false,
  pageSize = 25,
  height,
  label,
  emptyText = "No rows yet.",
  quickFilterText,
}: DataGridProps<T>) {
  const apiRef = useRef<GridApi<T> | null>(null);
  // Inline pinned add-row only when NOT using the modal-create pattern.
  const inlineAdd = Boolean(onAdd) && !addModal;
  const [draft, setDraft] = useState<T | null>(inlineAdd && addTemplate ? { ...addTemplate } : null);
  // Modal-create + guarded-delete state (Phase 4).
  const [modalDraft, setModalDraft] = useState<T | null>(null);
  const [modalBusy, setModalBusy] = useState(false);
  const [showCustomAdd, setShowCustomAdd] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<T | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const commitAdd = useCallback(async () => {
    if (!onAdd || !draft) return;
    const keep = await onAdd(draft);
    if (keep !== false && addTemplate) setDraft({ ...addTemplate }); // reset the add-row
  }, [onAdd, draft, addTemplate]);

  // Modal-create commit (Phase 4): submit the modal draft; keep the modal open when
  // onAdd rejects it (returns false — a validation failure).
  const commitModalAdd = useCallback(async () => {
    if (!onAdd || !modalDraft) return;
    setModalBusy(true);
    try {
      const keep = await onAdd(modalDraft);
      if (keep !== false) setModalDraft(null);
    } finally {
      setModalBusy(false);
    }
  }, [onAdd, modalDraft]);

  // Delete: guarded by a confirm when confirmDeleteMessage is set, else immediate.
  const requestDelete = useCallback(
    (row: T) => {
      if (confirmDeleteMessage) setPendingDelete(row);
      else if (onDelete) void onDelete(row);
    },
    [confirmDeleteMessage, onDelete],
  );
  const confirmDelete = useCallback(async () => {
    if (!onDelete || !pendingDelete) return;
    setDeleteBusy(true);
    try {
      await onDelete(pendingDelete);
      setPendingDelete(null);
    } finally {
      setDeleteBusy(false);
    }
  }, [onDelete, pendingDelete]);

  // The trailing action column: ＋ on the pinned add-row, ✕ on a data row.
  const actionCol: ColDef<T> = useMemo(
    () => ({
      colId: ACTION_COL,
      headerName: "",
      pinned: "right",
      width: 56,
      minWidth: 56,
      maxWidth: 56,
      sortable: false,
      filter: false,
      editable: false,
      resizable: false,
      suppressNavigable: false,
      cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
      cellRenderer: (p: ICellRendererParams<T>) => {
        if (p.node.rowPinned === "top") {
          if (!inlineAdd) return null;
          return (
            <button type="button" aria-label="Add row" title="Add (Enter)" onClick={() => void commitAdd()} style={btn("#c9a227")}>
              ＋
            </button>
          );
        }
        if (!onDelete || !p.data) return null;
        return (
          <button type="button" aria-label="Delete row" title="Delete row" onClick={() => requestDelete(p.data as T)} style={btn("#e8735a")}>
            ✕
          </button>
        );
      },
    }),
    [inlineAdd, onDelete, commitAdd, requestDelete],
  );

  // Columns: pinned add-row cells are always editable (so the add-row behaves like a
  // form), and empty pinned cells show the header as a placeholder.
  const colDefs = useMemo<ColDef<T>[]>(() => {
    const mapped = columns.map((c) => {
      const callerEditable = c.editable;
      const out: ColDef<T> = {
        ...c,
        editable: (params) =>
          params.node.rowPinned === "top"
            ? true
            : typeof callerEditable === "function"
              ? (callerEditable as (p: unknown) => boolean)(params)
              : Boolean(callerEditable),
      };
      const callerRenderer = c.cellRenderer;
      out.cellRendererSelector = (params) => {
        if (params.node.rowPinned === "top") {
          const empty = params.value === "" || params.value == null;
          if (empty) {
            return {
              component: () => (
                <span style={{ color: "var(--text-dim)", fontStyle: "italic" }}>{String(c.headerName ?? c.field ?? "")}</span>
              ),
            };
          }
        }
        return callerRenderer ? { component: callerRenderer } : undefined;
      };
      return out;
    });
    return inlineAdd || onDelete ? [...mapped, actionCol] : mapped;
  }, [columns, inlineAdd, onDelete, actionCol]);

  const onCellValueChanged = useCallback(
    (e: CellValueChangedEvent<T>) => {
      const field = e.colDef.field;
      if (!field) return;
      if (e.node.rowPinned === "top") {
        setDraft({ ...(e.data as T) }); // the pinned row IS the draft
        return;
      }
      if (onEdit && e.data) void onEdit(e.data as T, field, e.newValue);
    },
    [onEdit],
  );

  // Modal-create fields: derived from the columns that carry a `field` (the same
  // set the pinned add-row would edit). Phase 4.
  const modalFields = useMemo(
    () => columns.filter((c) => typeof c.field === "string" && c.field),
    [columns],
  );

  return (
    <>
    {addModal && (onAdd || renderAddModal) && (
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
        <Button
          size="sm"
          variant="accent"
          onClick={() =>
            renderAddModal
              ? setShowCustomAdd(true)
              : setModalDraft(addTemplate ? { ...addTemplate } : ({} as T))
          }
        >
          ＋ Add{addLabel ? ` ${addLabel}` : ""}
        </Button>
      </div>
    )}
    {showCustomAdd && renderAddModal?.({ close: () => setShowCustomAdd(false) })}
    {modalDraft && onAdd && (
      <AdminModal
        title={`Add${addLabel ? ` ${addLabel}` : " a row"}`}
        confirmLabel={modalBusy ? "Adding…" : "Add"}
        busy={modalBusy}
        onConfirm={() => void commitModalAdd()}
        onCancel={() => { if (!modalBusy) setModalDraft(null); }}
      >
        <div style={{ display: "grid", gap: 12, minWidth: 280 }}>
          {modalFields.map((c) => (
            <AddField
              key={c.field as string}
              col={c}
              value={(modalDraft as Record<string, unknown>)[c.field as string]}
              onChange={(v) =>
                setModalDraft((d) => ({ ...(d as object), [c.field as string]: v }) as T)
              }
            />
          ))}
        </div>
      </AdminModal>
    )}
    {pendingDelete && confirmDeleteMessage && (
      <ConfirmDialog
        title="Delete"
        destructive
        confirmLabel={deleteBusy ? "Deleting…" : "Delete"}
        busy={deleteBusy}
        onConfirm={() => void confirmDelete()}
        onCancel={() => { if (!deleteBusy) setPendingDelete(null); }}
      >
        <p style={{ margin: 0, fontSize: "var(--fs-sm)", color: "var(--text-2)" }}>
          {confirmDeleteMessage(pendingDelete)}
        </p>
      </ConfirmDialog>
    )}
    <div
      className="cave-grid"
      role="region"
      aria-label={label}
      style={height ? { height, width: "100%" } : { width: "100%" }}
      onKeyDown={(ev) => {
        const api = apiRef.current;
        if (!api) return;
        // Enter on the add-row commits it; Delete/Backspace on a focused data row deletes.
        const cell = api.getFocusedCell();
        const node = cell ? api.getDisplayedRowAtIndex(cell.rowIndex) : null;
        if (ev.key === "Enter" && cell?.rowPinned === "top" && !ev.shiftKey) {
          // let a cell edit commit first, then add
          setTimeout(() => void commitAdd(), 0);
        } else if ((ev.key === "Delete" || ev.key === "Backspace") && node && !node.rowPinned && onDelete) {
          const editing = api.getEditingCells().length > 0;
          if (!editing && node.data) {
            ev.preventDefault();
            requestDelete(node.data);
          }
        }
      }}
    >
      <AgGridReact<T>
        theme={caveGridTheme}
        rowData={rows}
        columnDefs={colDefs}
        getRowId={(p: GetRowIdParams<T>) => getRowId(p.data)}
        pinnedTopRowData={inlineAdd && draft ? [draft] : undefined}
        quickFilterText={quickFilterText}
        pagination={paginated}
        paginationPageSize={pageSize}
        paginationPageSizeSelector={paginated ? [10, 25, 50, 100] : undefined}
        domLayout={height ? "normal" : "autoHeight"}
        defaultColDef={{
          sortable: true,
          filter: true,
          floatingFilter: true,
          resizable: true,
          flex: 1,
          minWidth: 110,
          suppressHeaderMenuButton: true,
          cellClass: "cave-grid-cell",
        }}
        stopEditingWhenCellsLoseFocus
        singleClickEdit
        animateRows
        overlayNoRowsTemplate={`<span style="color:var(--text-dim)">${emptyText}</span>`}
        onCellValueChanged={onCellValueChanged}
        onGridReady={(e: GridReadyEvent<T>) => {
          apiRef.current = e.api;
        }}
      />
    </div>
    </>
  );
}

// AddField renders one modal-create form field derived from a column's ColDef
// (Phase 4): a <select> for agSelectCellEditor, a checkbox for agCheckboxCellEditor,
// else a text input. Label is the column's headerName.
function AddField<T extends object>({
  col,
  value,
  onChange,
}: {
  col: ColDef<T>;
  value: unknown;
  onChange: (v: unknown) => void;
}) {
  const field = col.field as string;
  const label = col.headerName ?? field;
  const editor = col.cellEditor as string | undefined;
  const labelStyle: React.CSSProperties = {
    fontFamily: "var(--font-mono)",
    fontSize: "var(--fs-2xs)",
    letterSpacing: "var(--ls-label)",
    textTransform: "uppercase",
    color: "var(--text-label, var(--text-3))",
  };
  const inputStyle: React.CSSProperties = {
    padding: "8px 10px",
    background: "var(--surface-1)",
    border: "1px solid var(--border)",
    borderRadius: "var(--radius-control, 6px)",
    color: "var(--text-1)",
    font: "inherit",
    fontSize: "var(--fs-sm)",
  };
  if (editor === "agCheckboxCellEditor") {
    return (
      <label style={{ display: "flex", alignItems: "center", gap: 8, ...labelStyle }}>
        <input type="checkbox" checked={Boolean(value)} onChange={(e) => onChange(e.target.checked)} />
        {label}
      </label>
    );
  }
  if (editor === "agSelectCellEditor") {
    const opts = ((col.cellEditorParams as { values?: string[] } | undefined)?.values ?? []) as string[];
    return (
      <label style={{ display: "grid", gap: 4 }}>
        <span style={labelStyle}>{label}</span>
        <select value={String(value ?? "")} onChange={(e) => onChange(e.target.value)} style={inputStyle}>
          <option value="">—</option>
          {opts.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      </label>
    );
  }
  return (
    <label style={{ display: "grid", gap: 4 }}>
      <span style={labelStyle}>{label}</span>
      <input
        type="text"
        value={String(value ?? "")}
        onChange={(e) => onChange(e.target.value)}
        style={inputStyle}
      />
    </label>
  );
}

function btn(color: string): React.CSSProperties {
  return {
    background: "transparent",
    border: "none",
    color,
    cursor: "pointer",
    fontSize: 15,
    lineHeight: 1,
    padding: 4,
  };
}
